import { useUpdateProfileImage } from '@features/profile/hooks/useUpdateProfileImage';
import { ClearRounded } from '@mui/icons-material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import { FC, useContext, useState } from 'react';

import { useEditTemplateImages } from '../hooks/useEditTemplateImages';

type UserDetailImageRowProps = {
  label: string;
  value?: string | string[] | null;
  tooltip?: string;
  action: () => void;
  template_id?: number;
};

const UserDetailImageRow: FC<UserDetailImageRowProps> = ({
  label,
  value,
  tooltip,
  action,
  template_id,
}) => {
  const { authData } = useContext(authContext);
  const isEnterprise = authData.user?.enterprise;
  const [errorText, setErrorText] = useState('');
  const { updateProfileImage, isUpdatingProfileImage } = useUpdateProfileImage({
    setAlertText: setErrorText,
  });
  const { editTemplateImages, isUpdatingTemplateImages } =
    useEditTemplateImages({
      setErrorText,
    });

  // Check if value is an array
  const isArray = Array.isArray(value);
  const hasValue = isArray ? value.length > 0 : Boolean(value);

  // For arrays, show first few images with count indicator
  const displayImages = isArray ? value.slice(0, 3) : [value as string];
  const isCoverImage = label === 'Background Image';

  // 👇 Handle remove cover image
  const handleRemoveCoverImage = () => {
    if (isEnterprise) {
      editTemplateImages({
        cover_image: 'delete',
        etemplate_id: template_id,
      });
    } else {
      updateProfileImage({ cover_image: 'delete' });
    }
  };

  return (
    <>
      <Grid
        container
        alignItems="center"
        bgcolor={white}
        borderRadius={8}
        py={2}
      >
        <Grid item xs={12} sm={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              fontWeight={medium}
              display="flex"
              gap={1}
              alignItems="center"
            >
              {label}:
              {tooltip && (
                <Tooltip title={tooltip} placement="top" arrow>
                  <HelpOutlineIcon
                    sx={{
                      fontSize: '18px',
                      color: 'grey.400',
                      transition: 'color 0.35s ease',
                      '&:hover': { color: 'grey.900' },
                    }}
                  />
                </Tooltip>
              )}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={4} justifyContent="center" display="flex">
          {isArray ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {displayImages?.length === 0 ? (
                <Avatar
                  src=""
                  style={{
                    width: '65px',
                    height: '65px',
                    borderRadius: 5,
                    border: '1px solid #f0f0f0',
                  }}
                />
              ) : (
                displayImages?.map((img, index) => (
                  <Avatar
                    key={index}
                    src={(img as string) || ''}
                    style={{
                      width: '65px',
                      height: '65px',
                      borderRadius: 5,
                      border: '1px solid #f0f0f0',
                    }}
                  />
                ))
              )}
            </Stack>
          ) : (
            <Box
              className={isCoverImage ? 'cover_image' : ''}
              sx={{
                position: 'relative',
              }}
            >
              <Avatar
                src={(value as string) || ''}
                style={{
                  width: '65px',
                  height: '65px',
                  borderRadius: 5,
                  opacity:
                    isUpdatingProfileImage || isUpdatingTemplateImages
                      ? '0.3'
                      : '',
                  border: '1px solid #f0f0f0',
                }}
              />
              {isCoverImage && value && (
                <Tooltip title="Remove Background Image" placement="top" arrow>
                  <IconButton
                    color="primary"
                    sx={{
                      fontSize: '15px',
                      color: '#fff',
                      transition: 'all 0.35s ease',
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      padding: '3px',
                      backgroundColor: 'red',
                      '&:hover': {
                        color: '#fff',
                        backgroundColor: '#c40808',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'red',
                        color: '#fff',
                        opacity: '0.3',
                      },
                    }}
                    onClick={handleRemoveCoverImage}
                    disabled={
                      isUpdatingProfileImage || isUpdatingTemplateImages
                    }
                  >
                    <ClearRounded
                      sx={{
                        fontSize: 'inherit',
                        color: 'inherit',
                        transition: 'inherit',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={4} justifyContent="end" display="flex">
          <Button
            id={`edit-button-${label.toLowerCase().replace(/\s+/g, '-')}`}
            color={hasValue ? 'primary' : 'success'}
            variant="contained"
            sx={{
              padding: '4px 15px',
              fontSize: '14px',
            }}
            startIcon={
              hasValue ? (
                <EditOutlinedIcon sx={{ fontSize: '16px' }} />
              ) : (
                <AddOutlinedIcon sx={{ fontSize: '16px' }} />
              )
            }
            onClick={action}
          >
            {hasValue ? 'Edit' : 'Add'}
          </Button>
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: '7px' }} />
    </>
  );
};

export default UserDetailImageRow;
